// Pure markdown formatters for "Copy script (LLM-friendly)" and "Copy
// storyboard (LLM-friendly)" actions. Shared between the Generated Ads
// history tab and the Product Mapping page so both produce identical
// clipboard output for the same storyboard.

import { Storyboard, StoryboardSegment } from './types';

function prettyEnum(val: string | null | undefined): string {
  if (!val) return '';
  return String(val).replace(/_/g, ' ');
}

function prettyDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function header(storyboard: Storyboard, productName: string | null): string[] {
  const lines: string[] = [];
  if (productName) lines.push(`Product: ${productName}`);
  if (storyboard.generationModel) lines.push(`Model: ${storyboard.generationModel}`);
  if (storyboard.createdAt) lines.push(`Generated: ${prettyDate(storyboard.createdAt)}`);
  if (storyboard.totalDurationSec != null) lines.push(`Total duration: ${storyboard.totalDurationSec}s`);
  if (Array.isArray(storyboard.referenceAdIds) && storyboard.referenceAdIds.length > 0) {
    lines.push(`Reference ad${storyboard.referenceAdIds.length === 1 ? '' : 's'}: ${storyboard.referenceAdIds.join(', ')}`);
  }
  return lines;
}

/**
 * Markdown export of the read-through flat script. Uses the snapshotted
 * flatScript on the storyboard (preferred) or reconstructs it from
 * segments as a fallback.
 */
export function formatScriptForLLM(
  storyboard: Storyboard,
  productName: string | null,
): string {
  const heading = `# Script — ${productName || `Storyboard ${storyboard.storyboardId.slice(0, 8)}`}`;
  const meta = header(storyboard, productName).join('\n');
  const script = (storyboard.flatScript && storyboard.flatScript.trim())
    || (storyboard.segments || [])
      .filter(s => s.scriptText?.trim())
      .map(s => s.scriptText.trim())
      .join('\n\n')
    || '(no script available)';
  return `${heading}\n\n${meta}\n\n---\n\n${script}\n`;
}

/**
 * Markdown export of the full storyboard — metadata header + per-segment
 * sections that another LLM can ingest as structured context.
 */
export function formatStoryboardForLLM(
  storyboard: Storyboard,
  productName: string | null,
): string {
  const heading = `# Storyboard — ${productName || `Storyboard ${storyboard.storyboardId.slice(0, 8)}`}`;
  const meta = header(storyboard, productName).join('\n');
  const segments = (storyboard.segments || [])
    .slice()
    .sort((a, b) => (a.segmentOrder ?? 0) - (b.segmentOrder ?? 0));
  const segmentBlocks = segments.map(formatSegmentBlock).join('\n\n');
  return `${heading}\n\n${meta}\n\n---\n\n${segmentBlocks || '(no segments)'}\n`;
}

function formatSegmentBlock(seg: StoryboardSegment): string {
  const role = prettyEnum(seg.segmentRole) || 'segment';
  const dur = seg.durationSec != null ? `${seg.durationSec}s` : '?';
  const lines: string[] = [];
  lines.push(`## Segment ${seg.segmentOrder}. ${role} — ${dur}`);
  if (seg.scriptText) lines.push(`- Script: "${seg.scriptText.trim()}"`);
  if (seg.visualDirection) lines.push(`- Visual direction: ${seg.visualDirection.trim()}`);
  if (seg.shotType) lines.push(`- Shot type: ${seg.shotType}`);
  lines.push(`- On-screen text: ${seg.onScreenText || '(none)'}`);
  if (seg.emotionalTone) lines.push(`- Emotional tone: ${prettyEnum(seg.emotionalTone)}`);
  if (seg.pacing) lines.push(`- Pacing: ${prettyEnum(seg.pacing)}`);
  if (seg.generationNotes) lines.push(`- Notes: ${seg.generationNotes.trim()}`);
  return lines.join('\n');
}
